import React from 'react';

/**
 * Parses simple markdown syntax and returns React elements
 * Supports: **bold**, *italic*, `code`
 */
export function parseSimpleMarkdown(text: string): React.ReactNode[] {
  if (!text) return [];
  
  const lines = text.split('\n');
  
  return lines.map((line, lineIndex) => {
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let keyCounter = 0;
    
    // Process the line character by character with regex
    const processLine = (text: string): React.ReactNode[] => {
      const result: React.ReactNode[] = [];
      let lastIndex = 0;
      
      // Combined regex for **bold**, *italic*, and `code`
      const combinedRegex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g;
      let match;
      
      while ((match = combinedRegex.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          result.push(text.slice(lastIndex, match.index));
        }
        
        if (match[1]) {
          // **bold**
          result.push(
            <strong key={`bold-${lineIndex}-${keyCounter++}`} className="font-semibold">
              {match[2]}
            </strong>
          );
        } else if (match[3]) {
          // *italic*
          result.push(
            <em key={`italic-${lineIndex}-${keyCounter++}`} className="italic">
              {match[4]}
            </em>
          );
        } else if (match[5]) {
          // `code`
          result.push(
            <code key={`code-${lineIndex}-${keyCounter++}`} className="bg-muted px-1 py-0.5 rounded text-xs font-mono">
              {match[6]}
            </code>
          );
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
      }
      
      return result;
    };
    
    parts.push(...processLine(remaining));
    
    // Add line break between lines (except for the last one)
    if (lineIndex < lines.length - 1) {
      parts.push(<br key={`br-${lineIndex}`} />);
    }
    
    return <React.Fragment key={`line-${lineIndex}`}>{parts}</React.Fragment>;
  });
}
